import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export interface SextforceCaptionRow {
    _id: string;
    subscriberId: string;
    caption: string;
    category?: string;
    usedCount: number;
}

export interface SextforceCaptionCategoryRow {
    _id: string;
    subscriberId: string;
    name: string;
}

const useSextforceCaptions = (queryEnabled?: boolean) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const axios = useAxios();
    const dialog = useDialog();
    const params = useParams();
    const queryClient = useQueryClient();
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [copyLoading, setCopyLoading] = useState(false);
    const [categoryMutationLoading, setCategoryMutationLoading] = useState(false);

    const base = () => `${settingsContext.routes.sextforce.base}${params.userId}/captions`;

    const fetchData = async (): Promise<SextforceCaptionRow[]> => {
        if (!userContext.jwtToken || !settingsContext.apiKey || !params.userId) return [];
        const data = await axios
            .get(base())
            .then(r => r.data as SextforceCaptionRow[])
            .catch(e => {
                handleHttpError(e, dialog);
                return [];
            });
        return data ?? [];
    };

    const enabled =
        (queryEnabled === undefined || queryEnabled === true) && !!(userContext.jwtToken && params.userId && settingsContext.apiKey);

    const query = useQuery(['sextforceCaptions', params.userId, userContext.jwtToken], fetchData, {
        refetchOnWindowFocus: false,
        staleTime: 60_000,
        enabled,
    });

    const fetchCategories = async (): Promise<SextforceCaptionCategoryRow[]> => {
        if (!userContext.jwtToken || !settingsContext.apiKey || !params.userId) return [];
        const data = await axios
            .get(`${base()}/categories`)
            .then(r => r.data as SextforceCaptionCategoryRow[])
            .catch(e => {
                handleHttpError(e, dialog);
                return [];
            });
        return data ?? [];
    };

    const categoriesQuery = useQuery(['sextforceCaptionCategories', params.userId, userContext.jwtToken], fetchCategories, {
        refetchOnWindowFocus: false,
        staleTime: 60_000,
        enabled,
    });

    const createCaption = async (caption: string, category: string = ''): Promise<SextforceCaptionRow | null> => {
        if (!params.userId) return null;
        const created = await axios
            .post(base(), { caption: caption.trim(), category: category.trim() })
            .then(r => r.data)
            .catch(e => {
                handleHttpError(e, dialog);
                return null;
            });
        if (created) queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        return created;
    };

    const updateCaption = async (captionId: string, caption: string, category: string = ''): Promise<SextforceCaptionRow | null> => {
        if (!params.userId) return null;
        const updated = await axios
            .put(`${base()}/${captionId}`, { caption: caption.trim(), category: category.trim() })
            .then(r => r.data)
            .catch(e => {
                handleHttpError(e, dialog);
                return null;
            });
        if (updated) queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        return updated;
    };

    const deleteCaption = async (captionId: string): Promise<boolean> => {
        if (!params.userId) return false;
        const ok = await axios
            .delete(`${base()}/${captionId}`)
            .then(() => true)
            .catch(e => {
                handleHttpError(e, dialog);
                return false;
            });
        if (ok) queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        return ok;
    };

    const bulkDeleteCaptions = async (captionIds: string[]): Promise<number> => {
        if (!params.userId || captionIds.length === 0) return 0;
        setBulkDeleteLoading(true);
        const result = await axios
            .delete(`${base()}/bulkDelete`, { data: { captionIds } })
            .then(r => r.data as { deleted: number })
            .catch(e => {
                handleHttpError(e, dialog);
                return { deleted: 0 };
            });
        setBulkDeleteLoading(false);
        if (result.deleted > 0) queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        return result.deleted;
    };

    const importFromCsv = async (captions: string[], category: string = ''): Promise<number> => {
        if (!params.userId || captions.length === 0) return 0;
        setImportLoading(true);
        const result = await axios
            .post(`${base()}/import`, { captions, category: category.trim() })
            .then(r => r.data as { imported: number })
            .catch(e => {
                handleHttpError(e, dialog);
                return { imported: 0 };
            });
        setImportLoading(false);
        if (result.imported > 0) queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        return result.imported;
    };

    const importFromCsvText = async (csvText: string, category: string = ''): Promise<number> => {
        if (!params.userId) return 0;
        setImportLoading(true);
        const result = await axios
            .post(`${base()}/import`, { csvText, category: category.trim() })
            .then(r => r.data as { imported: number })
            .catch(e => {
                handleHttpError(e, dialog);
                return { imported: 0 };
            });
        setImportLoading(false);
        if (result.imported > 0) queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        return result.imported;
    };

    const copyCaptionsToOtherAccounts = async (captionIds: string[], targetUserId: string): Promise<number> => {
        if (!params.userId || !targetUserId || captionIds.length === 0) return 0;
        setCopyLoading(true);
        const result = await axios
            .post(`${base()}/copyToOtherAccounts`, { targetUserId, captionIds })
            .then(r => r.data as { copied: number })
            .catch(e => {
                handleHttpError(e, dialog);
                return { copied: 0 };
            });
        setCopyLoading(false);
        // Only the source captions list is managed by this hook; we don't invalidate other accounts here.
        if (result.copied > 0) queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        return result.copied;
    };

    const assignCategory = async (captionIds: string[], category: string): Promise<number> => {
        if (!params.userId || captionIds.length === 0) return 0;
        setCategoryMutationLoading(true);
        const result = await axios
            .post(`${base()}/assignCategory`, { captionIds, category: category.trim() })
            .then(r => r.data as { updated: number })
            .catch(e => {
                handleHttpError(e, dialog);
                return { updated: 0 };
            });
        setCategoryMutationLoading(false);
        if (result.updated > 0) queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        return result.updated;
    };

    const createCategory = async (name: string): Promise<SextforceCaptionCategoryRow | null> => {
        if (!params.userId) return null;
        setCategoryMutationLoading(true);
        const created = await axios
            .post(`${base()}/categories`, { name: name.trim() })
            .then(r => r.data as SextforceCaptionCategoryRow)
            .catch(e => {
                handleHttpError(e, dialog);
                return null;
            });
        setCategoryMutationLoading(false);
        if (created) queryClient.invalidateQueries(['sextforceCaptionCategories', params.userId]);
        return created;
    };

    const updateCategory = async (categoryId: string, name: string): Promise<SextforceCaptionCategoryRow | null> => {
        if (!params.userId) return null;
        setCategoryMutationLoading(true);
        const updated = await axios
            .put(`${base()}/categories/${categoryId}`, { name: name.trim() })
            .then(r => r.data as SextforceCaptionCategoryRow)
            .catch(e => {
                handleHttpError(e, dialog);
                return null;
            });
        setCategoryMutationLoading(false);
        if (updated) {
            queryClient.invalidateQueries(['sextforceCaptionCategories', params.userId]);
            queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        }
        return updated;
    };

    const deleteCategory = async (categoryId: string): Promise<boolean> => {
        if (!params.userId) return false;
        setCategoryMutationLoading(true);
        const ok = await axios
            .delete(`${base()}/categories/${categoryId}`)
            .then(() => true)
            .catch(e => {
                handleHttpError(e, dialog);
                return false;
            });
        setCategoryMutationLoading(false);
        if (ok) {
            queryClient.invalidateQueries(['sextforceCaptionCategories', params.userId]);
            queryClient.invalidateQueries(['sextforceCaptions', params.userId]);
        }
        return ok;
    };

    return {
        ...query,
        categories: categoriesQuery.data || [],
        categoriesLoading: categoriesQuery.isLoading,
        createCaption,
        updateCaption,
        deleteCaption,
        bulkDeleteCaptions,
        bulkDeleteLoading,
        importFromCsv,
        importFromCsvText,
        importLoading,
        copyCaptionsToOtherAccounts,
        copyLoading,
        assignCategory,
        createCategory,
        updateCategory,
        deleteCategory,
        categoryMutationLoading,
    };
};

export default useSextforceCaptions;
